import React, { useEffect, useState, useRef } from "react";
import {
  Card,
  List,
  Avatar,
  Typography,
  Input,
  Button,
  message,
  Empty,
  Upload,
  Tooltip,
} from "antd";
import {
  SendOutlined,
  ReloadOutlined,
  PaperClipOutlined,
  DownloadOutlined,
  CheckOutlined,
  CheckCircleTwoTone,
} from "@ant-design/icons";
import axiosInstance from "../../axiosInstance";

const { Title, Text } = Typography;

const DOMAIN = "https://smts.org.in/";

export default function Supportchats() {
  const auth = JSON.parse(localStorage.getItem("auth_user") || "{}");
  const userId = auth?.id;
  const role = (auth?.role || "").toLowerCase();

  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [conversationId, setConversationId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMsg, setNewMsg] = useState("");
  const [sending, setSending] = useState(false);
  const [attachment, setAttachment] = useState(null);

  const messagesEndRef = useRef(null);
  const pollingRef = useRef(null);

  // Fetch all users except self
  const fetchUsers = async () => {
    try {
      const empRes = await axiosInstance.get("/all_employee.php");
      const convRes = await axiosInstance.get(
        `/chats/get_conversations.php?user_id=${userId}&role=${role}`
      );

      if (!empRes.data.success) return;

      const employees = empRes.data.data || [];
      const conversations = convRes.data.success ? convRes.data.data : [];

      // Map user → conversation_id if exists
      const merged = employees
        .filter((e) => String(e.id) !== String(userId)) // exclude self
        .map((emp) => {
          const conv = conversations.find(
            (c) =>
              (String(c.employee_id) === String(emp.id) &&
                String(c.admin_id) === String(userId)) ||
              (String(c.admin_id) === String(emp.id) &&
                String(c.employee_id) === String(userId))
          );

          return {
            ...emp,
            conversation_id: conv ? conv.conversation_id : null,
          };
        });

      setUsers(merged);
    } catch (err) {
      message.error("Failed to fetch users");
    }
  };

  // Find conversation for selected user
  const findConversationFor = async (user) => {
    if (!user) return null;
    try {
      const res = await axiosInstance.get(
        `/chats/get_conversations.php?user_id=${userId}&role=${role}`
      );
      if (res.data.success) {
        const conv = res.data.data?.find(
          (c) =>
            (String(c.employee_id) === String(user.id) &&
              String(c.admin_id) === String(userId)) ||
            (String(c.admin_id) === String(user.id) &&
              String(c.employee_id) === String(userId))
        );
        return conv ? conv.conversation_id : null;
      }
    } catch {
      return null;
    }
  };

  // Open a chat with selected user
  const openChat = async (user) => {
    setSelectedUser(user);
    setMessages([]);
    setConversationId(null);

    const existing = await findConversationFor(user);
    if (existing) {
      setConversationId(existing);
      fetchMessages(existing);
    }

    // Stop old polling
    if (pollingRef.current) clearInterval(pollingRef.current);

    // Start new polling for this chat
    pollingRef.current = setInterval(() => {
      if (conversationId) fetchMessages(conversationId, false);
    }, 3000);
  };

  // Fetch messages for the active conversation
  const fetchMessages = async (cid, scroll = true) => {
    if (!cid) return;
    try {
      const res = await axiosInstance.get(
        `/chats/get_messages.php?conversation_id=${cid}&user_id=${userId}`
      );
      if (res.data.success) {
        const newData = res.data.data || [];
        setMessages((prev) => {
          if (prev.length !== newData.length) return newData;
          return prev;
        });
        if (scroll) scrollToBottom();
      }
    } catch (err) {
      console.error(err);
    }
  };

  const scrollToBottom = () => {
    if (messagesEndRef.current)
      messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
  };

  // Create a new conversation if not exists
  const createConversation = async (employeeId, adminId) => {
    try {
      const res = await axiosInstance.post("/chats/create_conversation.php", {
        employee_id: employeeId,
        admin_id: adminId,
      });
      return res.data.success ? res.data.conversation_id : null;
    } catch {
      message.error("Conversation creation failed");
      return null;
    }
  };

  // Send a message
  const handleSend = async () => {
    if ((!newMsg.trim() && !attachment) || !selectedUser) return;
    setSending(true);
    try {
      let cid = conversationId;
      if (!cid) {
        const employeeId = role === "employee" ? userId : selectedUser.id;
        const adminId = role === "admin" ? userId : selectedUser.id;
        cid = await createConversation(employeeId, adminId);
        setConversationId(cid);
      }

      const fd = new FormData();
      fd.append("conversation_id", cid);
      fd.append("sender_id", userId);
      fd.append("message", newMsg);
      fd.append("is_admin", role === "admin" ? 1 : 0);
      if (attachment) fd.append("attachment", attachment);

      const res = await axiosInstance.post("/chats/send_message.php", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      if (res.data.success) {
        setNewMsg("");
        setAttachment(null);
        fetchMessages(cid);
      } else {
        message.error(res.data.message);
      }
    } catch (err) {
      message.error("Send failed");
    } finally {
      setSending(false);
    }
  };

  // Before upload
  const beforeUpload = (file) => {
    setAttachment(file);
    return false;
  };
  const removeAttachment = () => setAttachment(null);

  // Load all users initially
  useEffect(() => {
    fetchUsers();
    return () => pollingRef.current && clearInterval(pollingRef.current);
  }, []);

  // Load messages when conversation changes
  useEffect(() => {
    if (conversationId) fetchMessages(conversationId);
  }, [conversationId]);

  // Fix attachment URL from DB to proper domain
  const getFixedAttachmentURL = (path) => {
    if (!path) return null;
    if (path.startsWith("http")) return path;
    const fixed = path.replace(
      "api/uploads/support/",
      "api/chats/uploads/support/"
    );
    return `${DOMAIN}${fixed}`;
  };

  // WhatsApp-like seen tick icons
  const renderSeenTick = (isMine, isRead) => {
    if (!isMine) return null;
    return isRead ? (
      <CheckCircleTwoTone
        twoToneColor="#4fc3f7"
        style={{ fontSize: 14, marginLeft: 5 }}
      />
    ) : (
      <CheckOutlined style={{ fontSize: 13, color: "#999", marginLeft: 5 }} />
    );
  };

  return (
    <div className="p-5 bg-gray-50 rounded-lg" style={{ height: "90vh" }}>
      <Card bordered={false} style={{ borderRadius: 12, height: "100%" }}>
        <div className="flex justify-between mb-3 items-center">
          <Title level={4} style={{ margin: 0 }}>
            Help & Support
          </Title>
          <Button
            icon={<ReloadOutlined />}
            onClick={() => {
              fetchUsers();
              if (conversationId) fetchMessages(conversationId);
            }}
          >
            Refresh
          </Button>
        </div>

        <div className="flex h-full gap-4">
          {/* Left: Users */}
          <div className="w-80 border-r pr-3 overflow-y-auto h-[calc(100vh-220px)]">
            {users.length === 0 ? (
              <Empty description="No users" />
            ) : (
              <List
                dataSource={users}
                renderItem={(u) => (
                  <List.Item
                    onClick={() => openChat(u)}
                    style={{
                      cursor: "pointer",
                      padding: 10,
                      borderRadius: 8,
                      background:
                        selectedUser?.id === u.id ? "#e6f7ff" : "transparent",
                    }}
                  >
                    <List.Item.Meta
                      avatar={
                        <Avatar
                          src={
                            u.profile_pic
                              ? `${DOMAIN}api/${u.profile_pic.replace(
                                  /\\/g,
                                  "/"
                                )}`
                              : null
                          }
                        >
                          {!u.profile_pic && u.name?.[0]}
                        </Avatar>
                      }
                      title={<Text strong>{u.name}</Text>}
                      description={<Text type="secondary">{u.email}</Text>}
                    />
                  </List.Item>
                )}
              />
            )}
          </div>

          {/* Right: Chat */}
          <div className="flex-1 flex flex-col pl-3 h-[calc(100vh-220px)] overflow-hidden">
            {selectedUser ? (
              <>
                {/* Header */}
                <div className="flex items-center gap-3 border-b pb-2 mb-2 flex-shrink-0">
                  <Avatar
                    src={
                      selectedUser.profile_pic
                        ? `${DOMAIN}api/${selectedUser.profile_pic.replace(
                            /\\/g,
                            "/"
                          )}`
                        : null
                    }
                  >
                    {!selectedUser.profile_pic && selectedUser.name?.[0]}
                  </Avatar>
                  <div>
                    <Text strong>{selectedUser.name}</Text>
                    <br />
                    <Text type="secondary">{selectedUser.email}</Text>
                  </div>
                </div>

                {/* Scrollable Chat Area */}
                <div className="flex-1 overflow-y-auto bg-[#f9f9f9] rounded-md p-3 scroll-smooth">
                  {messages.length === 0 ? (
                    <Empty
                      description="No messages yet"
                      image={Empty.PRESENTED_IMAGE_SIMPLE}
                    />
                  ) : (
                    messages.map((m) => {
                      const mine = String(m.sender_id) === String(userId);
                      const fileURL = getFixedAttachmentURL(m.attachment);
                      return (
                        <div
                          key={m.id}
                          className={`flex mb-2 ${
                            mine ? "justify-end" : "justify-start"
                          }`}
                        >
                          <div
                            style={{
                              maxWidth: "70%",
                              background: mine ? "#dcf8c6" : "#fff",
                              padding: 10,
                              borderRadius: 10,
                              boxShadow: "0 1px 2px rgba(0,0,0,0.05)",
                            }}
                          >
                            {m.attachment ? (
                              /\.(jpg|jpeg|png|gif|webp)$/i.test(
                                m.attachment
                              ) ? (
                                <>
                                  <img
                                    src={fileURL}
                                    alt="attachment"
                                    style={{
                                      maxWidth: "100%",
                                      borderRadius: 8,
                                      marginBottom: 6,
                                    }}
                                  />
                                  {m.message && <div>{m.message}</div>}
                                </>
                              ) : (
                                <>
                                  <a href={fileURL} download>
                                    <Button
                                      icon={<DownloadOutlined />}
                                      size="small"
                                    >
                                      Download File
                                    </Button>
                                  </a>
                                  {m.message && <div>{m.message}</div>}
                                </>
                              )
                            ) : (
                              <div>{m.message}</div>
                            )}
                            <div
                              style={{
                                fontSize: 11,
                                color: "#999",
                                textAlign: "right",
                                marginTop: 4,
                              }}
                            >
                              {m.created_at}
                              {renderSeenTick(mine, m.is_read == 1)}
                            </div>
                          </div>
                        </div>
                      );
                    })
                  )}
                  <div ref={messagesEndRef} />
                </div>

                {/* Input Area */}
                <div className="flex-shrink-0 mt-3">
                  <div className="flex items-center gap-2">
                    <Upload beforeUpload={beforeUpload} showUploadList={false}>
                      <Tooltip title="Attach file">
                        <Button icon={<PaperClipOutlined />} />
                      </Tooltip>
                    </Upload>
                    <Input.TextArea
                      rows={2}
                      placeholder="Type a message..."
                      value={newMsg}
                      onChange={(e) => setNewMsg(e.target.value)}
                      onPressEnter={(e) => {
                        if (!e.shiftKey) {
                          e.preventDefault();
                          handleSend();
                        }
                      }}
                    />
                    <Button
                      type="primary"
                      icon={<SendOutlined />}
                      loading={sending}
                      onClick={handleSend}
                    >
                      Send
                    </Button>
                  </div>

                  {attachment && (
                    <div style={{ marginTop: 6 }}>
                      <Text type="secondary">📎 {attachment.name}</Text>
                      <Button
                        size="small"
                        onClick={removeAttachment}
                        style={{ marginLeft: 8 }}
                      >
                        Remove
                      </Button>
                    </div>
                  )}
                </div>
              </>
            ) : (
              <div className="flex justify-center items-center flex-1 text-gray-400">
                Select a user to start chatting
              </div>
            )}
          </div>
        </div>
      </Card>
    </div>
  );
}
